import { ApiService } from '../ApiService';
import type { JournalEntry } from '../../../types/accounting';
import type { BusinessUnitFilterBase } from '../../../types/filters';
import type { PaginatedResponse } from '../types';

/**
 * Paramètres de recherche pour les entrées de l'agent
 */
export interface AgentEntrySearchParams extends BusinessUnitFilterBase {
  page?: number;
  pageSize?: number;
  status?: 'pending' | 'validated' | 'rejected' | 'all';
  fiscalYearId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export const agentEntriesApi = {
  /**
   * Récupérer toutes les entrées proposées par l'agent comptable
   * @param params.businessUnitId - Filtrer par unité commerciale (optionnel)
   * @param params.fiscalYearId - Filtrer par exercice fiscal (optionnel)
   */
  getAll: (params?: AgentEntrySearchParams) => 
    ApiService.get<PaginatedResponse<JournalEntry>>('/agent-entries', params),

  /**
   * Récupérer une entrée spécifique
   */
  getById: (id: string) => 
    ApiService.get<JournalEntry>(`/agent-entries/${id}`),

  /**
   * Valider une entrée - la transfère vers le journal principal
   * @see POST /agent-entries/:id/validate
   * @param id - ID de l'entrée à valider
   * @param modifications - Modifications à appliquer avant validation (optionnel)
   * 
   * Format du body:
   * - {} pour valider sans modification
   * - { modifications: { description, reference, lines, ... } } pour modifier avant validation
   */
  validate: (id: string, modifications?: Partial<JournalEntry>) => 
    ApiService.post<{
      id: string;
      validationStatus: 'validated';
      validatedBy: string;
      validatedAt: string;
      journalEntryId: string;
    }>(`/agent-entries/${id}/validate`, modifications ? { modifications } : {}),

  /**
   * Rejeter une entrée avec une raison
   * @see POST /agent-entries/:id/reject
   */
  reject: (id: string, reason: string) => 
    ApiService.post<void>(`/agent-entries/${id}/reject`, { reason }),

  /**
   * Valider plusieurs entrées en une seule opération
   * @see POST /agent-entries/batch/validate
   * 
   * @param ids - Liste des IDs des agent entries à valider
   * @param options - Options de validation (optionnel)
   */
  validateBatch: (ids: string[], options?: { skipLowConfidence?: boolean; minConfidence?: number }) => 
    ApiService.post<{
      validated: number;
      skipped: number;
      failed: number;
      results: Array<{
        id: string;
        status: 'validated' | 'skipped' | 'failed';
        journalEntryId?: string;
        error?: string;
      }>;
    }>('/agent-entries/batch/validate', { ids, ...(options && { options }) }),

  /**
   * Modifier une entrée avant validation
   */
  update: (id: string, entry: Partial<JournalEntry>) => 
    ApiService.put<JournalEntry>(`/agent-entries/${id}`, entry),

  /**
   * Supprimer une entrée proposée
   */
  delete: (id: string) => 
    ApiService.delete<void>(`/agent-entries/${id}`),

  /**
   * Obtenir les statistiques des entrées de l'agent
   * @param params.businessUnitId - Filtrer par unité commerciale (optionnel)
   */
  getStats: (params?: BusinessUnitFilterBase & { fiscalYearId?: string }) =>
    ApiService.get<{
      total: number;
      pending: number;
      validated: number;
      rejected: number;
    }>('/agent-entries/stats', params)
};
